import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Layers, Library } from "lucide-react";
import { MoleculeCanvas } from "@/components/MoleculeCanvas";
import { Marquee } from "@/components/Marquee";
import { FadeIn, MaskedLines, Eyebrow } from "@/components/Reveal";
import { services } from "@/data/services";
import { useTitle } from "@/hooks/use-title";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614308459036-779d0dfe51ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxzY2llbnRpZmljJTIwbGFib3JhdG9yeSUyMHJlc2VhcmNoZXIlMjBiaW9sb2d5JTIwaGVhbHRoY2FyZSUyMG1pY3Jvc2NvcGV8ZW58MHx8fHwxNzg4NjY4ODU0fDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { value: "06", label: "Service Domains" },
  { value: "100%", label: "Confidentiality" },
  { value: "360°", label: "R&D Lifecycle Support" },
];

const stakeholders = [
  "Clinicians",
  "Researchers",
  "Universities",
  "CROs",
  "Startups",
  "Pharma Companies",
  "Biotech Companies",
];

const whyPreview = [
  {
    icon: Layers,
    title: "Multidisciplinary Expertise",
    copy: "Medical writers, clinicians, statisticians, and pharmaceutical scientists working as one team on your outcome.",
  },
  {
    icon: ShieldCheck,
    title: "Strict Confidentiality",
    copy: "Every project is handled under rigorous confidentiality with uncompromising data integrity — 100%, always.",
  },
  {
    icon: Library,
    title: "Journal & Regulatory Mastery",
    copy: "A working command of journal expectations and regulatory frameworks that shortens the path to acceptance.",
  },
];

export default function Home() {
  useTitle("THREENNOVATORS LLP — Scientific, Clinical & Pharmaceutical Consultancy");
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute -right-40 -top-40 h-[30rem] w-[30rem] rounded-full bg-coral-100/60 blur-3xl"
          aria-hidden="true"
        />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-16 pt-14 sm:px-6 sm:pt-20 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-24 lg:pt-24">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <Eyebrow data-testid="hero-eyebrow">
                Bridging Science, Medicine &amp; Innovation
              </Eyebrow>
            </motion.div>
            <h1
              className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
              data-testid="hero-headline"
            >
              <MaskedLines
                lines={[
                  "Advancing Science",
                  "Through Knowledge,",
                  <>
                    <span className="italic text-coral-500">Innovation</span>{" "}
                    &amp; Expertise.
                  </>,
                ]}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
              data-testid="hero-subtext"
            >
              End-to-end scientific, clinical, pharmaceutical, and research
              support — helping ideas evolve into credible, compliant, and
              impactful outcomes.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/services"
                data-testid="hero-explore-services-button"
                className="group inline-flex items-center gap-2 rounded-full bg-coral-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-coral-600"
              >
                Explore Our Services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-discuss-project-button"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/60 px-7 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:border-coral-400 hover:text-coral-600"
              >
                Discuss Your Project
              </Link>
            </motion.div>
            <motion.dl
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="mt-14 grid grid-cols-3 gap-6 border-t border-slate-200 pt-8"
              data-testid="hero-stats"
            >
              {stats.map((s) => (
                <div key={s.label}>
                  <dd className="font-serif text-2xl text-slate-900 sm:text-3xl">
                    {s.value}
                  </dd>
                  <dt className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 sm:text-[11px]">
                    {s.label}
                  </dt>
                </div>
              ))}
            </motion.dl>
          </div>

          <div className="relative lg:col-span-5" ref={imgRef}>
            <MoleculeCanvas className="absolute -inset-8 h-[calc(100%+4rem)] w-[calc(100%+4rem)]" />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-coral-500/10"
              data-testid="hero-image-frame"
            >
              <motion.img
                style={{ y }}
                src={HERO_IMG}
                alt="Scientific researcher examining samples under a microscope in a modern laboratory"
                className="h-[26rem] w-full scale-[1.15] object-cover sm:h-[30rem]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="absolute -bottom-6 left-2 rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 shadow-xl backdrop-blur-xl sm:-left-8"
              data-testid="hero-floating-card"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral-600">
                Trusted Partnership
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                Across the entire R&amp;D lifecycle
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <FadeIn className="lg:col-span-6">
            <Eyebrow>Who We Are</Eyebrow>
            <h2 className="mt-5 font-serif text-2xl leading-tight tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              A multidisciplinary consultancy at the intersection of{" "}
              <span className="italic text-coral-500">science, medicine</span>{" "}
              &amp; communication.
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-6">
            <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
              THREENNOVATORS LLP supports clinicians, researchers, universities,
              CROs, startups, and pharmaceutical and biotechnology companies
              throughout the research and development lifecycle.
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5" data-testid="home-stakeholder-chips">
              {stakeholders.map((s) => (
                <span
                  key={s}
                  className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-600 transition-colors duration-300 hover:border-coral-300 hover:text-coral-600"
                >
                  {s}
                </span>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors hover:text-coral-600"
            >
              More about us
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <FadeIn className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>What We Do</Eyebrow>
              <h2 className="mt-5 font-serif text-2xl leading-tight tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Six domains of <span className="italic text-coral-500">expertise.</span>
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="home-all-services-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors hover:text-coral-600"
            >
              View all services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeIn>
          <div className="mt-12 border-t border-slate-200" data-testid="home-services-list">
            {services.map((s, i) => (
              <FadeIn key={s.id} delay={i * 0.05} y={16}>
                <Link
                  to="/services"
                  data-testid={`home-service-row-${i + 1}`}
                  className="group grid grid-cols-12 items-center gap-4 border-b border-slate-200 py-6 transition-colors duration-300 hover:bg-coral-50/50 sm:py-7"
                >
                  <span className="col-span-2 font-mono text-xs tracking-[0.2em] text-coral-500 sm:col-span-1">
                    {s.index}
                  </span>
                  <span className="col-span-9 font-serif text-lg text-slate-900 transition-transform duration-300 group-hover:translate-x-2 sm:col-span-10 sm:text-2xl">
                    {s.title}
                  </span>
                  <ArrowUpRight className="col-span-1 h-5 w-5 justify-self-end text-slate-300 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-coral-500" />
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section id="products" className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8" data-testid="products-section">
          <FadeIn className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Our Products</Eyebrow>
              <h2 className="mt-5 max-w-2xl font-serif text-2xl leading-tight tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Tools we build for the{" "}
                <span className="italic text-coral-500">research community.</span>
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
              Beyond consultancy, we craft products that solve the problems we
              see in labs and manuscripts every day.
            </p>
          </FadeIn>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <FadeIn delay={0.05} className="h-full">
              <a
                href="https://www.manuscriptnest.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-card-manuscript-nest"
                className="group flex h-full flex-col rounded-[2rem] border border-slate-200 bg-[#FAFAFA] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-coral-300 hover:bg-white sm:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral-600">
                    Product — 01
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                    manuscriptnest.com
                  </span>
                </div>
                <h3 className="mt-10 font-serif text-3xl tracking-tight text-slate-900 sm:text-4xl">
                  Manuscript Nest
                </h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600 sm:text-base">
                  Professional manuscript editing services — expert editors
                  refine structure, language, and journal readiness, so your
                  research lands where it deserves.
                </p>
                <span className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors duration-300 group-hover:text-coral-600">
                  Visit Manuscript Nest
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </a>
            </FadeIn>

            <FadeIn delay={0.15} className="h-full">
              <a
                href="https://www.resynote.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-card-resynote"
                className="group flex h-full flex-col rounded-[2rem] bg-slate-950 p-8 transition-all duration-300 hover:-translate-y-1 sm:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral-400">
                    Product — 02
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    resynote.com
                  </span>
                </div>
                <h3 className="mt-10 font-serif text-3xl tracking-tight text-white sm:text-4xl">
                  ResyNote
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">
                  A research notebook for the modern lab — plan experiments,
                  track protocols, log observations, and export manuscript-ready
                  reports. Every tool your lab notebook never had.
                </p>
                <div className="mt-6 flex flex-1 flex-wrap content-start gap-2">
                  {["PDF / DOCX Export", "Cloud Sync", "Timeline & Reminders", "Version History"].map((f) => (
                    <span
                      key={f}
                      className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <span className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-coral-400 transition-colors duration-300 group-hover:text-coral-300">
                  Visit ResyNote
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </a>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
        <FadeIn>
          <Eyebrow>Why Threennovators</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-2xl leading-tight tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Built on rigour, delivered with{" "}
            <span className="italic text-coral-500">discretion.</span>
          </h2>
        </FadeIn>
        <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="home-why-cards">
          {whyPreview.map((w, i) => (
            <FadeIn
              key={w.title}
              delay={i * 0.1}
              className="group rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-coral-300"
            >
              <w.icon className="h-7 w-7 text-coral-500" strokeWidth={1.6} />
              <h3 className="mt-6 font-serif text-xl text-slate-900">{w.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.copy}</p>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.2}>
          <Link
            to="/why-us"
            data-testid="home-why-us-link"
            className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors hover:text-coral-600"
          >
            Read the full manifesto
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </FadeIn>
      </section>

      <section className="bg-slate-950">
        <div className="mx-auto max-w-5xl px-4 py-24 text-center sm:px-6 sm:py-32">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-coral-400">
              Our Vision
            </p>
            <p
              className="mt-8 font-serif text-2xl leading-snug text-white sm:text-3xl lg:text-4xl"
              data-testid="home-vision-statement"
            >
              “To be a trusted knowledge and innovation partner — bridging{" "}
              <span className="italic text-coral-400">science, medicine</span>{" "}
              and real-world impact through high-quality scientific
              communication.”
            </p>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
              100% Confidentiality · Always
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
        <FadeIn className="relative overflow-hidden rounded-[2.5rem] border border-coral-200 bg-coral-50 px-6 py-16 text-center sm:px-16">
          <h2 className="font-serif text-3xl tracking-tight text-slate-900 sm:text-4xl">
            Have a project in mind?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-600">
            Tell us where you are in the lifecycle — we will map the fastest
            credible route to your outcome.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="home-cta-discuss-project"
              className="group inline-flex items-center gap-2 rounded-full bg-coral-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-coral-600"
            >
              Discuss Your Project
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="home-cta-explore-services"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:border-coral-400 hover:text-coral-600"
            >
              Explore Our Services
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
